import type {
    PaddleCheckoutConfig,
    PaddleEvent,
    PaddleEventCallback,
    PaddlePriceData,
    PaddleLocationData,
    PriceBatches
} from '../types/paddle.types';

declare const Paddle: any;

interface PaddleCheckoutDeps {
    config: any; // Use any for now to avoid circular dependencies
    userRepository?: any;
    statsManager?: any;
    paywallRenderer?: any;
}

/**
 * Paddle Checkout Module - Consolidated Paddle SDK operations
 * Handles all Paddle SDK initialization, event handling, and checkout operations
 */
export class PaddleCheckout {
    private initialized = false;
    private checkoutInstance: boolean | null = null;
    public locationData: PaddleLocationData | null = null;
    public pricesData: Record<string, PaddlePriceData> = {};
    private pricesDataWithDiscount: Record<string, PaddlePriceData> = {};
    private eventCallbacks: PaddleEventCallback[] = [];
    private currentExpressMode = false;
    private currentExpressVariant: 'express' | 'legacy' = 'express';
    private currentCheckoutConfig: PaddleCheckoutConfig | null = null;
    private checkoutLoadedAt: number | null = null;
    private popupDetectionInstalled = false;
    public pricesFetchComplete = false;
    private deps: PaddleCheckoutDeps;

    constructor(deps: PaddleCheckoutDeps) {
        this.deps = deps;
    }

    /**
     * Initialize Paddle SDK
     */
    async init(): Promise<void> {
        if (this.initialized) {
            console.log('Paddle already initialized');
            return;
        }

        try {
            console.log('Initializing Paddle...');

            const token = this.deps.config.paddle.token;
            if (!token) {
                console.error('Paddle token not configured');
                return;
            }

            const isSandbox = token.startsWith('test_') || this.deps.config.environment === 'sandbox';
            console.log('Initializing Paddle with token:', token.substring(0, 10) + '...', 'Sandbox:', isSandbox);

            if (Paddle.Environment && Paddle.Environment.set) {
                Paddle.Environment.set(isSandbox ? 'sandbox' : 'production');
                console.log('Set Paddle environment to:', isSandbox ? 'sandbox' : 'production');
            } else {
                console.error('Paddle.Environment.set not available - Paddle SDK may not be loaded properly');
            }

            await Paddle.Initialize({ token });
            this.initialized = true;
            console.log('Paddle initialized successfully');

            this.setupEventHandling();
            this.installPopupDetection();
        } catch (error) {
            console.error('Paddle initialization error:', error);
        }
    }

    /**
     * Setup event handling
     */
    private setupEventHandling(): void {
        if (!this.initialized) {
            console.error('Paddle not initialized');
            return;
        }

        Paddle.Update({
            eventCallback: (event: PaddleEvent) => {
                this.handlePaddleEvent(event);
            }
        });
    }

    /**
     * Detect when Apple Pay / Google Pay falls back to the popup (non-inline) path.
     * Paddle launches that flow via window.open to a paddle.com domain; the inline path
     * never calls window.open. We wrap it once to log which path was taken and how long
     * after checkout.loaded the tap happened (the "first-tap too early" readiness race).
     */
    private installPopupDetection(): void {
        if (this.popupDetectionInstalled) return;
        this.popupDetectionInstalled = true;

        const originalOpen = window.open.bind(window);
        window.open = (url?: string | URL, target?: string, features?: string): Window | null => {
            try {
                const urlStr = url ? String(url) : '';
                // Attribute to Apple Pay only when a checkout is open and the popup targets a
                // Paddle domain — that's the popup (non-inline) path we're hunting.
                if (this.checkoutInstance && /paddle\.com/i.test(urlStr)) {
                    const msSinceCheckoutLoaded =
                        this.checkoutLoadedAt != null ? Date.now() - this.checkoutLoadedAt : -1;
                    console.warn(
                        `[ApplePay] POPUP path: window.open ${msSinceCheckoutLoaded}ms after ` +
                        `checkout.loaded (mode=${this.currentExpressVariant}) -> ${urlStr}`
                    );
                    this.deps.statsManager?.track('express_checkout_popup_opened', {
                        mode: this.currentExpressVariant,
                        msSinceCheckoutLoaded,
                        url: urlStr,
                    });
                }
            } catch (e) {
                console.error('[ApplePay] popup detection error', e);
            }
            return originalOpen(url, target, features);
        };
    }

    /**
     * Handle Paddle events
     */
    private handlePaddleEvent(event: PaddleEvent): void {
        console.log(`[Paddle ${new Date().toISOString()}] event:`, event.name);

        // Notify all registered callbacks
        this.eventCallbacks.forEach(callback => {
            try {
                callback(event);
            } catch (error) {
                console.error('Error in Paddle event callback:', error);
            }
        });

        const data = event.data || event;

        switch(event.name) {
            case 'checkout.loaded':
                this.checkoutLoadedAt = Date.now();
                console.log(
                    '[ApplePay] checkout.loaded (express:', this.currentExpressMode,
                    'variant:', this.currentExpressVariant + ')'
                );
                this.deps.statsManager?.track('express_checkout_loaded', {
                    mode: this.currentExpressVariant,
                    express: this.currentExpressMode,
                });
                break;

            case 'checkout.payment.error':
                console.error('Checkout error:', data);
                // Only run our manual Apple/Google Pay fallback for legacy express mode.
                // New express mode (variant:'express') renders its own "pay another way"
                // fallback via showNonExpressPaymentMethods, so we must not fight it.
                if (this.currentExpressMode && this.currentExpressVariant === 'legacy') {
                    this.handleExpressCheckoutUnavailable();
                }
                break;

            case 'checkout.customer.created':
            case 'checkout.customer.updated':
                if ((data as any).customer?.email && this.deps.userRepository) {
                    this.deps.userRepository.setEmail((data as any).customer.email);
                }
                this.handleCheckoutUpdate(data);
                break;

            case 'checkout.updated':
                this.handleCheckoutUpdate(data);
                break;

            case 'checkout.completed':
                console.log('Checkout completed successfully');
                this.handleCheckoutComplete(data);
                break;

            case 'checkout.closed':
                console.log('Checkout closed');
                this.checkoutInstance = null;
                this.checkoutLoadedAt = null;
                this.handleCheckoutClosed();
                break;
        }
    }

    /**
     * Handle checkout updates (country/currency changes)
     */
    private handleCheckoutUpdate(data: any): void {
        console.log('Checkout updated - Currency:', data.currency_code);

        if (data.currency_code) {
            if (data.customer?.address) {
                this.locationData = {
                    countryCode: data.customer.address.country_code,
                    postalCode: data.customer.address.postal_code
                };
            }

            if (this.deps.paywallRenderer?.updatePricesFromEvent) {
                this.deps.paywallRenderer.updatePricesFromEvent(data);
            }
        }
    }

    /**
     * Handle checkout completion
     */
    private handleCheckoutComplete(data: any): void {
        const amount = parseFloat(data.totals?.total || 0) / 100;
        const currency = data.currency_code || 'USD';

        if (this.deps.statsManager) {
            this.deps.statsManager.trackSubscriptionCompleted(data.id, amount, currency);
        }

        // Navigate to success screen
        const app = (window as any).app;
        if (app && typeof app.showScreen === 'function') {
            app.showScreen('success');
        } else {
            console.error('app.showScreen not available - cannot navigate to success screen');
        }
    }

    /**
     * Handle checkout closed
     */
    private handleCheckoutClosed(): void {
        console.log('Checkout closed via Paddle event');
    }

    /**
     * Register event callback
     */
    onEvent(callback: PaddleEventCallback): void {
        this.eventCallbacks.push(callback);
    }

    /**
     * Fetch prices from Paddle with batched requests and discount support
     */
    async fetchPrices(priceBatches: PriceBatches | string[]): Promise<any[]> {
        if (!this.initialized) {
            console.error('Paddle not initialized');
            return [];
        }

        this.pricesFetchComplete = false;

        // Handle backward compatibility
        let batches: PriceBatches;
        if (Array.isArray(priceBatches)) {
            console.log('Converting legacy array format to batch format');
            batches = {
                no_discount: {
                    priceIds: priceBatches,
                    discountId: null
                }
            };
        } else {
            batches = priceBatches;
        }

        if (!batches || Object.keys(batches).length === 0) {
            console.log('No price batches to fetch');
            this.pricesFetchComplete = true;
            return [];
        }

        try {
            // Check for manual location override in URL
            const urlParams = new URLSearchParams(window.location.search);
            const urlCountryCode = urlParams.get('countryCode');
            const urlPostalCode = urlParams.get('postalCode');

            if (urlCountryCode) {
                this.locationData = {
                    countryCode: urlCountryCode.toUpperCase(),
                    postalCode: urlPostalCode || ''
                };
                console.log('Using location from URL parameters:', this.locationData);
            }

            // Collect all unique price IDs for original prices
            const allPriceIds = new Set<string>();
            for (const batch of Object.values(batches)) {
                if (batch.priceIds) {
                    batch.priceIds.forEach(id => allPriceIds.add(id));
                }
            }

            // Fetch original prices (without any discounts)
            if (allPriceIds.size > 0) {
                console.log(`Fetching original prices for ${allPriceIds.size} items without discounts`);

                const originalPriceRequest: any = {
                    items: Array.from(allPriceIds).map(priceId => ({
                        priceId,
                        quantity: 1
                    }))
                };

                if (this.locationData) {
                    originalPriceRequest.address = {
                        countryCode: this.locationData.countryCode,
                        postalCode: this.locationData.postalCode
                    };
                }

                const originalPriceData = await Paddle.PricePreview(originalPriceRequest);
                console.log('Original price data fetched:', originalPriceData);

                this.storeOriginalPrices(originalPriceData);

                if (originalPriceData?.data?.address && !this.locationData) {
                    this.locationData = {
                        countryCode: originalPriceData.data.address.countryCode,
                        postalCode: originalPriceData.data.address.postalCode || ''
                    };
                    console.log('Detected location:', this.locationData);
                }
            }

            // Process each batch with discounts
            const allResults: any[] = [];
            for (const [batchKey, batch] of Object.entries(batches)) {
                if (!batch.priceIds || batch.priceIds.length === 0) {
                    continue;
                }

                if (!batch.discountId) {
                    console.log(`Skipping batch '${batchKey}' - no discount, using original prices`);
                    continue;
                }

                console.log(`Fetching ${batch.priceIds.length} prices for batch '${batchKey}' with discount ${batch.discountId}`);

                const previewRequest: any = {
                    items: batch.priceIds.map(priceId => ({
                        priceId,
                        quantity: 1
                    })),
                    discountId: batch.discountId
                };

                if (this.locationData) {
                    previewRequest.address = {
                        countryCode: this.locationData.countryCode,
                        postalCode: this.locationData.postalCode
                    };
                }

                const priceData = await Paddle.PricePreview(previewRequest);
                console.log(`Discounted price data fetched for batch '${batchKey}':`, priceData);

                this.processDiscountedPrices(priceData, batch.discountId);
                allResults.push(priceData);
            }

            this.pricesFetchComplete = true;
            console.log('All prices (including discounts) fetched successfully');

            return allResults;
        } catch (error) {
            console.error('Failed to fetch prices:', error);
            this.pricesFetchComplete = true;
            return [];
        }
    }

    /**
     * Store original prices (without any discounts)
     */
    private storeOriginalPrices(priceData: any): void {
        if (!priceData?.data?.details?.lineItems) {
            console.log('No line items to process for original prices');
            return;
        }

        const currency = priceData.data.currencyCode || 'USD';
        console.log(`Storing ${priceData.data.details.lineItems.length} original prices in ${currency}`);

        priceData.data.details.lineItems.forEach((item: any) => {
            const priceId = item.price?.id;
            if (priceId) {
                const totalCents = parseInt(item.totals?.total) || 0;
                const totalDollars = totalCents / 100;
                const subtotalCents = parseInt(item.totals?.subtotal) || 0;
                const subtotalDollars = subtotalCents / 100;
                const taxCents = parseInt(item.totals?.tax) || 0;
                const taxDollars = taxCents / 100;
                const unitPriceCents = parseInt(item.price?.unitPrice?.amount) || 0;

                // Detect tax mode: if subtotal equals unit price, tax is added on top (exclusive)
                // If subtotal < unit price, tax is included in the price (inclusive)
                const isTaxExclusive = subtotalCents === unitPriceCents;

                this.pricesData[priceId] = {
                    originalCents: totalCents,
                    originalDollars: totalDollars,
                    originalFormatted: item.formattedTotals?.total || '',
                    cents: totalCents,
                    dollars: totalDollars,
                    formatted: item.formattedTotals?.total || '',
                    subtotalCents,
                    subtotalDollars,
                    subtotalFormatted: item.formattedTotals?.subtotal || '',
                    taxCents,
                    taxDollars,
                    taxFormatted: item.formattedTotals?.tax || '',
                    isTaxExclusive,
                    originalSubtotalCents: subtotalCents,
                    originalSubtotalDollars: subtotalDollars,
                    discountPercentage: 0,
                    hasDiscount: false,
                    currency,
                    unitPrice: unitPriceCents,
                    billingCycle: item.price?.billingCycle,
                    trialPeriod: item.price?.trialPeriod,
                    description: item.price?.description,
                    taxRate: parseFloat(item.taxRate) || 0
                };

                console.log(`Stored original price for ${priceId}: subtotal=${subtotalCents}, tax=${taxCents}, total=${totalCents}, isTaxExclusive=${isTaxExclusive}`);
            }
        });
    }

    /**
     * Process and store discounted prices
     */
    private processDiscountedPrices(priceData: any, discountId: string): void {
        if (!priceData?.data?.details?.lineItems) {
            console.log('No line items to process for discounted prices');
            return;
        }

        const currency = priceData.data.currencyCode || 'USD';
        console.log(`Processing ${priceData.data.details.lineItems.length} discounted prices in ${currency} with discount ${discountId}`);

        priceData.data.details.lineItems.forEach((item: any) => {
            const priceId = item.price?.id;
            if (priceId) {
                const totalCents = parseInt(item.totals?.total) || 0;
                const totalDollars = totalCents / 100;
                // Note: Paddle's subtotal is BEFORE discount, so we need to subtract the discount
                const rawSubtotalCents = parseInt(item.totals?.subtotal) || 0;
                const discountAmountCents = parseInt(item.totals?.discount) || 0;
                // Actual subtotal after discount (what customer pays before tax)
                const subtotalCents = rawSubtotalCents - discountAmountCents;
                const subtotalDollars = subtotalCents / 100;
                const taxCents = parseInt(item.totals?.tax) || 0;
                const taxDollars = taxCents / 100;
                const unitPriceCents = parseInt(item.price?.unitPrice?.amount) || 0;

                // Detect tax mode: compare RAW subtotal (before discount) to unit price
                // If equal, tax is added on top (exclusive); if less, tax is included
                const isTaxExclusive = rawSubtotalCents === unitPriceCents;

                let discountPercentage = 0;
                if (item.discounts && item.discounts.length > 0) {
                    const discountInfo = item.discounts[0];
                    if (discountInfo.discount?.type === 'percentage') {
                        discountPercentage = parseFloat(discountInfo.discount.amount);
                    }
                }

                const originalData = this.pricesData[priceId];
                if (!originalData) {
                    console.warn(`Original price data not found for ${priceId}`);
                    return;
                }

                const compositeKey = `${priceId}:${discountId}`;

                this.pricesDataWithDiscount[compositeKey] = {
                    originalCents: originalData.originalCents,
                    originalDollars: originalData.originalDollars,
                    originalFormatted: originalData.originalFormatted,
                    cents: totalCents,
                    dollars: totalDollars,
                    formatted: item.formattedTotals?.total || '',
                    subtotalCents,  // Now correctly reflects discounted amount
                    subtotalDollars,
                    subtotalFormatted: item.formattedTotals?.subtotal || '',  // Note: this is still Paddle's pre-discount formatted
                    taxCents,
                    taxDollars,
                    taxFormatted: item.formattedTotals?.tax || '',
                    isTaxExclusive,
                    originalSubtotalCents: originalData.originalSubtotalCents,
                    originalSubtotalDollars: originalData.originalSubtotalDollars,
                    discountPercentage,
                    discountId,
                    hasDiscount: true,
                    currency,
                    unitPrice: unitPriceCents,
                    billingCycle: item.price?.billingCycle,
                    trialPeriod: item.price?.trialPeriod,
                    description: item.price?.description,
                    taxRate: parseFloat(item.taxRate) || 0
                };

                console.log(`Stored discounted price with key ${compositeKey}: rawSubtotal=${rawSubtotalCents}, discount=${discountAmountCents}, subtotal=${subtotalCents}, tax=${taxCents}, total=${totalCents}, isTaxExclusive=${isTaxExclusive}`);
            }
        });
    }

    /**
     * Get price data for a specific price ID and discount combination
     */
    getPriceData(priceId: string, discountId: string | null = null): PaddlePriceData | null {
        if (!priceId) return null;

        if (discountId) {
            const compositeKey = `${priceId}:${discountId}`;
            const discountedData = this.pricesDataWithDiscount[compositeKey];
            if (discountedData) {
                return discountedData;
            }
            console.warn(`Discounted price data not found for ${compositeKey}, falling back to original`);
        }

        return this.pricesData[priceId] || null;
    }

    /**
     * Open Paddle checkout
     */
    openCheckout(config: PaddleCheckoutConfig, express_mode = false): void {
        if (!this.initialized) {
            console.error('Paddle not initialized');
            return;
        }

        try {
            console.log('Opening Paddle checkout with config:', config, 'Express mode:', express_mode);

            this.currentExpressMode = express_mode;
            this.currentCheckoutConfig = config;

            // Build items array - support both legacy priceId and new items array
            const items = config.items && config.items.length > 0
                ? config.items.map(item => ({ priceId: item.priceId, quantity: item.quantity || 1 }))
                : config.priceId
                    ? [{ priceId: config.priceId, quantity: 1 }]
                    : [];

            if (items.length === 0) {
                console.error('No items provided for checkout');
                return;
            }

            console.log('Checkout items:', items);

            // Track checkout initiated with first item's price
            const firstPriceId = items[0]?.priceId;
            if (this.deps.statsManager && firstPriceId) {
                const priceData = this.pricesData[firstPriceId];
                if (priceData) {
                    this.deps.statsManager.trackCheckoutInitiated(priceData.dollars, priceData.currency);
                }
            }

            const checkoutConfig: any = {
                items: items,
                settings: {
                    displayMode: config.displayMode || 'inline',
                    theme: 'light',
                    showAddTaxId: false,
                    allowDiscountRemoval: false,
                    showAddDiscounts: false,
                    frameTarget: config.frameTarget || 'paddle-checkout',
                    frameInitialHeight: 450,
                    frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
                    successUrl: config.successUrl || this.getSuccessUrl()
                }
            };

            if (express_mode) {
                // Resolve which express implementation to use. A ?expressVariant=express|legacy
                // URL param overrides the config default (mirrors the discountCode/countryCode
                // URL overrides below), so either mode can be forced on any domain for testing.
                const urlExpress = new URLSearchParams(window.location.search).get('expressVariant');
                const expressVariant: 'express' | 'legacy' =
                    urlExpress === 'express' || urlExpress === 'legacy'
                        ? urlExpress
                        : this.deps.config.paddle.expressVariant;
                this.currentExpressVariant = expressVariant;

                if (expressVariant === 'legacy') {
                    // LEGACY EXPRESS MODE — Apple Pay / Google Pay only via allowedPaymentMethods.
                    // Superseded by Paddle's official express checkout; kept as a fallback switch.
                    checkoutConfig.settings.allowedPaymentMethods = ['apple_pay', 'google_pay'];
                    checkoutConfig.settings.frameInitialHeight = 120;
                } else {
                    // NEW EXPRESS MODE — Paddle official express checkout (variant:'express', 2026-04-27).
                    // Requires displayMode:'inline' (set above) + Apple Pay domain verification.
                    checkoutConfig.settings.variant = 'express';
                    checkoutConfig.settings.showNonExpressPaymentMethods = true; // keep "pay another way"
                    checkoutConfig.settings.frameInitialHeight = 182;
                }
                console.log('Express checkout variant:', expressVariant);
            }

            if (config.email) {
                checkoutConfig.customer = {
                    email: config.email
                };
                if (this.locationData) {
                    checkoutConfig.customer.address = {
                        countryCode: this.locationData.countryCode,
                        postalCode: this.locationData.postalCode
                    };
                }
            } else {
                checkoutConfig.customer = undefined;
            }

            if (config.customData) {
                checkoutConfig.customData = config.customData;
            }

            if (config.discountId) {
                checkoutConfig.discountId = config.discountId;
            } else if (config.discountCode) {
                checkoutConfig.discountCode = config.discountCode;
            }

            // Check URL for discount override
            const urlParams = new URLSearchParams(window.location.search);
            const urlDiscountCode = urlParams.get('discountCode');
            const urlDiscountId = urlParams.get('discountId');

            if (urlDiscountCode) {
                checkoutConfig.discountCode = urlDiscountCode;
                console.log('Applying discount code from URL:', urlDiscountCode);
            } else if (urlDiscountId) {
                checkoutConfig.discountId = urlDiscountId;
                console.log('Applying discount ID from URL:', urlDiscountId);
            }

            Paddle.Checkout.open(checkoutConfig);
            this.checkoutInstance = true;
            console.log('Checkout opened successfully');
        } catch (error) {
            console.error('Failed to open checkout:', error);
        }
    }

    /**
     * Close checkout
     */
    closeCheckout(): void {
        if (this.checkoutInstance) {
            this.checkoutInstance = null;
            console.log('Checkout close requested');

            if (typeof Paddle.Checkout.close === 'function') {
                Paddle.Checkout.close();
            }
        }
    }

    /**
     * Update checkout with new items/price
     */
    updateCheckout(config: Partial<PaddleCheckoutConfig>): boolean {
        if (!this.checkoutInstance) {
            console.error('No checkout instance to update');
            return false;
        }

        try {
            console.log('Updating checkout with new config:', config);

            // Build items array - support both legacy priceId and new items array
            const items = config.items && config.items.length > 0
                ? config.items.map(item => ({ priceId: item.priceId, quantity: item.quantity || 1 }))
                : config.priceId
                    ? [{ priceId: config.priceId, quantity: 1 }]
                    : [];

            if (items.length === 0) {
                console.error('No items provided for checkout update');
                return false;
            }

            const updateConfig: any = {
                items: items
            };

            if (config.discountId) {
                updateConfig.discountId = config.discountId;
            } else if (config.discountCode) {
                updateConfig.discountCode = config.discountCode;
            }

            // Update stored config
            if (this.currentCheckoutConfig) {
                this.currentCheckoutConfig.items = config.items;
                this.currentCheckoutConfig.priceId = config.priceId;
                if (config.discountId) {
                    this.currentCheckoutConfig.discountId = config.discountId;
                } else if (config.discountCode) {
                    this.currentCheckoutConfig.discountCode = config.discountCode;
                }
            }

            console.log('Update config being sent:', updateConfig);

            Paddle.Checkout.updateCheckout(updateConfig);

            console.log('Checkout updated successfully');
            return true;
        } catch (error) {
            console.error('Failed to update checkout - Error details:', error);
            return false;
        }
    }

    /**
     * Handle express checkout unavailable
     */
    private handleExpressCheckoutUnavailable(): void {
        console.log('Express checkout unavailable, showing "Get my plan" button');

        this.closeCheckout();

        const inlineContainer = document.getElementById('paddle-inline-checkout');
        if (inlineContainer) {
            inlineContainer.style.display = 'none';
        }

        const planButton = document.getElementById('get-my-plan-btn');
        if (planButton) {
            planButton.style.display = 'block';
        }
    }

    /**
     * Open checkout with all payment methods in popup
     */
    openCheckoutPopup(): void {
        console.log('Opening checkout popup with all payment methods');

        const overlay = document.getElementById('checkout-popup-overlay');
        if (overlay) {
            overlay.style.display = 'flex';
            overlay.removeAttribute('aria-hidden');
        }

        // Note: This calls PaywallRenderer which should be in deps
        // For now, we'll access it globally
        if (window.PaywallRenderer) {
            window.PaywallRenderer.openPaddleCheckout('popup-paddle-checkout');
        }
    }

    /**
     * Get success URL (post-payment redirect target).
     *
     * Must NOT contain a '#': Paddle's checkout app copies this value into its own
     * internal router `search` field, and React Router rejects a literal '#' there
     * ("Cannot include a '#' character in a manually specified `to.search` field"),
     * which crashes the whole checkout. So we signal success with a `paddle_success=1`
     * query param that the funnel translates back to the success screen on load
     * (see FlowManager.startNavigation).
     */
    private getSuccessUrl(): string {
        let href = window.location.href;
        if (href.includes('file://')) {
            href = "https://vocalist.learn2sing.us/";
        }
        const url = new URL(href);
        url.hash = '';
        url.searchParams.set('paddle_success', '1');
        return url.toString();
    }

    /**
     * Format price with currency
     */
    formatPrice(amount: number, currency: string): string {
        const localeMap: Record<string, string> = {
            'USD': 'en-US', 'EUR': 'de-DE', 'GBP': 'en-GB', 'CAD': 'en-CA',
            'AUD': 'en-AU', 'JPY': 'ja-JP', 'CHF': 'de-CH', 'SEK': 'sv-SE',
            'DKK': 'da-DK', 'NOK': 'nb-NO', 'PLN': 'pl-PL', 'CZK': 'cs-CZ',
            'HUF': 'hu-HU', 'RON': 'ro-RO', 'BGN': 'bg-BG', 'HRK': 'hr-HR',
            'RUB': 'ru-RU', 'TRY': 'tr-TR', 'CNY': 'zh-CN', 'IDR': 'id-ID',
            'INR': 'en-IN', 'KRW': 'ko-KR', 'MXN': 'es-MX', 'MYR': 'ms-MY',
            'NZD': 'en-NZ', 'PHP': 'en-PH', 'SGD': 'en-SG', 'THB': 'th-TH',
            'ZAR': 'en-ZA', 'ISK': 'is-IS', 'HKD': 'zh-HK', 'TWD': 'zh-TW',
            'ARS': 'es-AR', 'CLP': 'es-CL', 'COP': 'es-CO', 'PEN': 'es-PE',
            'UYU': 'es-UY'
        };

        const locale = localeMap[currency] || 'en-US';

        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(amount);
        } catch (error) {
            return `${currency} ${amount.toFixed(2)}`;
        }
    }
}

// Global type augmentation
declare global {
    interface Window {
        PaywallRenderer?: any;
    }
}
